import { html } from 'htm/preact'
import { useState, useEffect } from 'preact/hooks'
import { api } from '../api.js'
import { lineCostPreview, money, getCompatibleUnits } from '../units.js'
import PortionCalc from '../components/PortionCalc.js'
import IngredientPicker from '../components/IngredientPicker.js'
import UnitPicker from '../components/UnitPicker.js'

function computeTotal(lines, ingredients) {
  let total = 0
  lines.forEach(line => {
    const ing = ingredients.find(x => x.id === line.ingredient_id)
    if (!ing || !line.quantity || line.quantity <= 0) return
    const p = lineCostPreview(line.quantity, line.unit, ing.price_per_unit, ing.unit)
    if (!p.error) total += p.cost
  })
  return total
}

function lineCostStr(line, ing) {
  if (!ing || !line.quantity || line.quantity <= 0) return '—'
  const p = lineCostPreview(line.quantity, line.unit, ing.price_per_unit, ing.unit)
  return p.error ? '—' : money(p.cost)
}

// Pure form component — Sheet lives in Recipes.js so onClose works reliably
export default function RecipeEditor({ id, onCancel, onSaved, showToast, ingredients }) {
  const isNew = id === 'new'

  const [name, setName] = useState('')
  const [category, setCategory] = useState('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState([])
  const [totalBatchWeightKg, setTotalBatchWeightKg] = useState('')
  const [servingSizeGrams, setServingSizeGrams] = useState('')
  const [salesUnit, setSalesUnit] = useState('piece')
  const [piecesPerSaleUnit, setPiecesPerSaleUnit] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setName('')
    setCategory('')
    setNotes('')
    setLines([])
    setPiecesPerSaleUnit('')
    if (!id || isNew) return

    setLoading(true)
    api(`/api/recipes/${id}`)
      .then(d => {
        const r = d.recipe
        setName(r.name || '')
        setCategory(r.category || '')
        setNotes(r.notes || '')
        setTotalBatchWeightKg(r.total_batch_weight_kg != null ? String(r.total_batch_weight_kg) : '')
        setServingSizeGrams(r.serving_size_grams != null ? String(r.serving_size_grams) : '')
        setSalesUnit(r.sales_unit || 'piece')
        setPiecesPerSaleUnit(r.pieces_per_sale_unit != null ? String(r.pieces_per_sale_unit) : '')
        setLines((r.lines || []).map(l => ({
          ingredient_id: l.ingredient_id,
          quantity: l.quantity,
          unit: l.unit,
        })))
      })
      .catch(e => showToast(e.message, true))
      .finally(() => setLoading(false))
  }, [id])

  const total = computeTotal(lines, ingredients)

  function addLine() {
    setLines([...lines, {
      ingredient_id: '',
      quantity: '',
      unit: 'g',
    }])
  }

  function handleSelectIngredient(idx, ingId, ingUnit) {
    setLines(lines.map((l, i) => i === idx ? { ...l, ingredient_id: ingId, unit: ingUnit || l.unit } : l))
  }

  function removeLine(idx) {
    setLines(lines.filter((_, i) => i !== idx))
  }

  function updateLine(idx, field, val) {
    setLines(lines.map((l, i) => i === idx ? { ...l, [field]: val } : l))
  }

  async function handleSave() {
    const nm = name.trim()
    if (!nm) { showToast('Name required', true); return }
    let tbw = null
    if (totalBatchWeightKg !== '') {
      tbw = parseFloat(totalBatchWeightKg)
      if (!Number.isFinite(tbw) || tbw <= 0) { showToast('Total batch weight must be positive or empty', true); return }
    }
    let ssg = null
    if (servingSizeGrams !== '') {
      ssg = parseFloat(servingSizeGrams)
      if (!Number.isFinite(ssg) || ssg <= 0) { showToast('Serving size must be positive or empty', true); return }
    }
    let ppsu = null
    if (piecesPerSaleUnit !== '') {
      ppsu = parseFloat(piecesPerSaleUnit)
      if (!Number.isFinite(ppsu) || ppsu <= 0) { showToast('Pieces per sale unit must be positive or empty', true); return }
    }
    const su = salesUnit === 'tray' || salesUnit === 'weight' ? salesUnit : 'piece'
    const body = {
      name: nm,
      category: category.trim() || null,
      total_batch_weight_kg: tbw,
      serving_size_grams: ssg,
      sales_unit: su,
      pieces_per_sale_unit: ppsu,
      notes: notes.trim() || null,
      lines: lines.filter(l => l.ingredient_id && l.quantity > 0),
    }
    try {
      if (isNew) {
        await api('/api/recipes', { method: 'POST', body: JSON.stringify(body) })
        showToast('Recipe created')
      } else {
        await api(`/api/recipes/${id}`, { method: 'PUT', body: JSON.stringify(body) })
        showToast('Saved')
      }
      onSaved && onSaved()
    } catch (e) {
      showToast(e.message, true)
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this recipe?')) return
    try {
      await api(`/api/recipes/${id}`, { method: 'DELETE' })
      showToast('Deleted')
      onSaved && onSaved()
    } catch (e) {
      showToast(e.message, true)
    }
  }

  const allCats = [...new Set(ingredients.map(i => i.category).filter(Boolean))].sort()

  if (loading) return html`<div class="empty">Loading…</div>`

  return html`
    <div class="field">
      <label>Name</label>
      <input class="input" value=${name} onInput=${e => setName(e.target.value)} placeholder="e.g. Chocolate cake" autoComplete="off" />
    </div>
    <div class="row-2">
      <div class="field">
        <label>Category</label>
        <input
          class="input"
          list="recipe-cats"
          value=${category}
          onInput=${e => setCategory(e.target.value)}
          placeholder="e.g. Desserts"
          autoComplete="off"
        />
        <datalist id="recipe-cats">
          ${allCats.map(c => html`<option value=${c} />`)}
        </datalist>
      </div>
    </div>
    <div class="field">
      <label>Notes (optional)</label>
      <textarea class="input" rows="2" value=${notes} onInput=${e => setNotes(e.target.value)} placeholder="e.g. Proof 2h, freeze day 2…" />
    </div>

    <p class="section-label">Yield &amp; Pricing</p>
    <div class="row-2">
      <div class="field">
        <label>Produced as</label>
        <select class="input" value=${salesUnit} onChange=${e => setSalesUnit(e.target.value)}>
          <option value="piece">Piece</option>
          <option value="tray">Tray</option>
          <option value="weight">Weight</option>
        </select>
      </div>
      ${salesUnit === 'tray' ? html`
        <div class="field">
          <label>Total batch weight (kg)</label>
          <input class="input" type="number" min="0.001" step="0.001" value=${totalBatchWeightKg} onInput=${e => setTotalBatchWeightKg(e.target.value)} placeholder="e.g. 1.8" />
        </div>
      ` : null}
    </div>
    ${salesUnit === 'tray' ? html`
      <div class="field">
        <label>Pieces per tray</label>
        <input class="input" type="number" min="1" step="1" value=${piecesPerSaleUnit} onInput=${e => setPiecesPerSaleUnit(e.target.value)} placeholder="e.g. 30" />
      </div>
    ` : null}
    <div class="field">
      <label>Serving size (grams, optional)</label>
      <input class="input" type="number" min="1" step="1" value=${servingSizeGrams} onInput=${e => setServingSizeGrams(e.target.value)} placeholder="e.g. 150 (optional)" />
    </div>

    <p class="section-label">Ingredients</p>
    ${lines.length === 0 && html`<p class="empty-sm">No lines yet.</p>`}
    <div class="lines-list">
      ${lines.map((line, idx) => {
        const ing = ingredients.find(i => i.id === line.ingredient_id)
        return html`
          <div class="line-row" key=${idx}>
            <div class="line-ing-picker">
              <${IngredientPicker}
                ingredients=${ingredients}
                value=${line.ingredient_id}
                onSelect=${(ingId, ingName, ingUnit) => handleSelectIngredient(idx, ingId, ingUnit)}
              />
            </div>
            <input
              class="input line-qty"
              type="number"
              min="0.0001"
              step="any"
              value=${line.quantity}
              onInput=${e => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
            />
            ${(() => {
              const compatibleUnits = ing ? getCompatibleUnits(ing.unit) : null
              return compatibleUnits && compatibleUnits.length > 1
                ? html`<${UnitPicker}
                    value=${line.unit}
                    onChange=${u => updateLine(idx, 'unit', u)}
                    compatibleUnits=${compatibleUnits}
                  />`
                : html`<select
                    class="input line-unit"
                    value=${line.unit}
                    onChange=${e => updateLine(idx, 'unit', e.target.value)}
                  >
                    ${['kg', 'g', 'lb', 'L', 'ml', 'count', 'piece'].map(u => html`<option value=${u}>${u}</option>`)}
                  </select>`
            })()}
            <span class="line-cost">${lineCostStr(line, ing)}</span>
            <button type="button" class="icon-btn" onClick=${() => removeLine(idx)} aria-label="Remove">✕</button>
          </div>
        `
      })}
    </div>
    <button type="button" class="btn btn--ghost btn--block" onClick=${addLine}>+ Add ingredient</button>

    <${PortionCalc}
      id=${id}
      recipe=${{
        total_batch_weight_kg: totalBatchWeightKg ? parseFloat(totalBatchWeightKg) : null,
        serving_size_grams: servingSizeGrams ? parseFloat(servingSizeGrams) : null,
        sales_unit: salesUnit,
        pieces_per_sale_unit: piecesPerSaleUnit ? parseFloat(piecesPerSaleUnit) : null,
      }}
      totalCost=${total}
    />

    <div class="sheet__actions">
      <button type="button" class="btn btn--ghost" onClick=${onCancel}>Cancel</button>
      <button type="button" class="btn btn--primary" onClick=${handleSave}>${isNew ? 'Create' : 'Save'}</button>
    </div>
    ${!isNew ? html`
      <button type="button" class="btn btn--danger btn--block" style="margin-top:0.5rem" onClick=${handleDelete}>
        Delete recipe
      </button>
    ` : null}
  `
}
